import type { ColDef, ICellRendererParams } from "ag-grid-community";
import type { ComponentProps } from "react";
import React from "react";

import { Link } from "@/components/Link";
import { StatusBadge } from "@/components/ui/icons/status";
import { format as formatAppDate } from "@/lib/utils/date";

const DATE_TIME_FIELD_PATTERN = /(_at$|^received_at$|^day$)/i;
const DATE_ONLY_FIELD_PATTERN = /(_date$|^accession_date$)/i;

const INSIGHTS_HIDDEN_COLUMNS = new Set(["subject_id"]);

const INSIGHTS_COLUMN_HEADER_NAMES: Record<string, string> = {
  sample_display_id: "Sample display ID",
  accession_id: "Accession ID",
  case_id: "Donor ID",
  unos_id: "UNOS ID",
};

function insightsColumnHeaderName(field: string): string {
  return (
    INSIGHTS_COLUMN_HEADER_NAMES[field] ??
    field.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
  );
}

function isInsightsNumericValue(value: unknown): boolean {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "bigint") return true;
  if (value != null && typeof value === "object" && "toNumber" in value) {
    return typeof (value as { toNumber?: unknown }).toNumber === "function";
  }
  return false;
}

function isInsightsDateOnlyField(field: string): boolean {
  return (
    DATE_ONLY_FIELD_PATTERN.test(field) && !DATE_TIME_FIELD_PATTERN.test(field)
  );
}

function formatInsightsDateValue(value: unknown, field: string): string {
  if (!(value instanceof Date)) return "";
  return formatAppDate(value, { time: !isInsightsDateOnlyField(field) }) ?? "";
}

function formatInsightsCellValue(value: unknown, field: string): string {
  if (value == null) return "";
  if (value instanceof Date) {
    return formatInsightsDateValue(value, field);
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "number") {
    return Number.isInteger(value) ? String(value) : value.toLocaleString();
  }
  if (typeof value === "bigint") {
    return value.toString();
  }
  if (typeof value === "object" && "toNumber" in value) {
    const toNumber = (value as { toNumber?: () => number }).toNumber;
    if (typeof toNumber === "function") {
      const numeric = toNumber.call(value);
      return Number.isInteger(numeric)
        ? String(numeric)
        : numeric.toLocaleString();
    }
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

type AccessionStatus = ComponentProps<typeof StatusBadge>["status"];

function AccessionIdCell({ value }: { value: unknown }) {
  if (value == null || value === "") return null;
  const id = String(value);
  return (
    <Link to="/accessions/$id" params={{ id }} className="tabular-nums">
      {id}
    </Link>
  );
}

const MemoizedAccessionIdCell = React.memo(AccessionIdCell);

function DonorIdCell({
  value,
  subjectId,
}: {
  value: unknown;
  subjectId: unknown;
}) {
  if (value == null || value === "") return null;
  const label = String(value);
  if (subjectId == null || subjectId === "") {
    return <span>{label}</span>;
  }
  return (
    <Link to="/subjects/$id" params={{ id: String(subjectId) }}>
      {label}
    </Link>
  );
}

const MemoizedDonorIdCell = React.memo(DonorIdCell);

function AccessionStatusCell({ value }: { value: unknown }) {
  if (value == null || value === "") return null;
  const status = String(value)
    .replace(/ /g, "_")
    .toLowerCase() as AccessionStatus;
  return (
    <div className="flex h-full items-center">
      <StatusBadge status={status} />
    </div>
  );
}

const MemoizedAccessionStatusCell = React.memo(AccessionStatusCell);

function insightsColumnWidth(
  field: string,
  isDate: boolean,
  isNumber: boolean,
) {
  if (field === "accession_id") return 130;
  if (field === "case_id") return 150;
  if (field === "accession_status") return 150;
  if (field === "all_client_orgs") return 220;
  if (isDate) return isInsightsDateOnlyField(field) ? 120 : 160;
  if (isNumber) return 120;
  return 160;
}

export function buildInsightsColumnDefs(
  rows: Record<string, unknown>[],
): ColDef<Record<string, unknown>>[] {
  if (rows.length === 0) return [];

  const keys = Object.keys(rows[0]).filter(
    (field) => !INSIGHTS_HIDDEN_COLUMNS.has(field),
  );

  return keys.map((field) => {
    const sample = rows.find((row) => row[field] != null)?.[field];
    const isDate =
      sample instanceof Date ||
      DATE_TIME_FIELD_PATTERN.test(field) ||
      DATE_ONLY_FIELD_PATTERN.test(field);
    const isNumber = isInsightsNumericValue(sample);
    const width = insightsColumnWidth(field, isDate, isNumber);

    const column: ColDef<Record<string, unknown>> = {
      field,
      headerName: insightsColumnHeaderName(field),
      minWidth: width,
      width,
      sortable: true,
      cellDataType: isNumber ? "number" : isDate ? "date" : "text",
      filter: isNumber ? "agNumberColumnFilter" : "agTextColumnFilter",
      filterParams: { maxNumConditions: 1 },
      valueFormatter: ({ value }) => formatInsightsCellValue(value, field),
      cellClass: isNumber ? "tabular-nums" : undefined,
      ...(isDate
        ? {
            filter: "agDateColumnFilter",
          }
        : {}),
    };

    if (field === "accession_id") {
      column.cellRenderer = ({ value }: ICellRendererParams) => (
        <MemoizedAccessionIdCell value={value} />
      );
    }

    if (field === "case_id") {
      column.cellRenderer = ({ value, data }: ICellRendererParams) => (
        <MemoizedDonorIdCell value={value} subjectId={data?.subject_id} />
      );
    }

    if (field === "accession_status") {
      column.cellRenderer = ({ value }: ICellRendererParams) => (
        <MemoizedAccessionStatusCell value={value} />
      );
    }

    return column;
  });
}
